import React, {Component, ReactElement, ReactNode} from 'react';
import {LeftRowFlexContainer} from '../../../styledComponents/FlexComponents';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faExternalLinkAlt} from '@fortawesome/free-solid-svg-icons';
import {Box} from '@mui/material';
import {Nullable} from '../../../app/TypeHelpers';
import styled from 'styled-components';
import {Colors, FontSize, LineHeight} from '../../../styledComponents/Constants';
import {currentMedia} from '../../../styledComponents/ResponsiveConvenience';
import {StyledDiv, StyledLi} from '../../../styledComponents/StyledTypeDefinitions';
import {UnstyledLinkStyledButton} from '../../../styledComponents/Button';

interface Props {
    mode: 'guest' | 'resident'
}

export class NatExternalTransmissionForm extends Component<Props> {
    constructor(props: Props) {
        super(props);

        this.externalTransmissionLinkClick = this.externalTransmissionLinkClick.bind(this);
        this.optoutLinkClick = this.optoutLinkClick.bind(this);
    }

    externalTransmissionLinkClick(): void {
        window.open('https://business.safety.google/intl/ja/tba-jp/', '_blank');
    };

    optoutLinkClick(): void {
        window.open('https://tools.google.com/dlpage/gaoptout?hl=ja', '_blank')
    }

    renderMainClause(): ReactNode {
        return (
            <>
                <TitleDiv>
                    利用者情報の外部送信について
                </TitleDiv>
                <MainClauseDiv>
                    <div>素敵ネットでは、ウェブサイトを提供するにあたって外部の事業者のサービス（以下 「外部サービス」といいます）を使用しており、
                        それらの使用において素敵ネットをご利用される方に関する情報を外部に送信しています。
                    </div>
                    <div>それぞれ送信先の外部サービスを提供する事業者のプライバシーポリシー等に基づいて管理・利用されています。</div>
                    <div>外部サービスにおいて大きな変更を行う際に見直し、改訂を行います。</div>
                </MainClauseDiv>
            </>
        )
    }

    renderTable(): ReactNode {
        return (
            <InfoArea>
                <StyledHeader $mode={this.props.mode}>
                    外部送信先と送信情報
                </StyledHeader>
                <DescriptionDiv>
                    <div>素敵ネットにおいて利用者情報を送信している外部サービスは以下の通りです。</div>
                    <div>なお、オプトアウトについて記載があるものについては、Cookieの無効化やオプトアウトを行うことができます。具体的には、後記のウェブページをご覧ください。</div>
                </DescriptionDiv>
                <InfoContainer>
                    <InfoItem>
                        <InfoTitleDiv>提供会社</InfoTitleDiv>
                        <InfoDiv>Google LLC</InfoDiv>
                    </InfoItem>
                    <InfoItem>
                        <InfoTitleDiv>サービス名</InfoTitleDiv>
                        <InfoDiv>
                            <InfoContentDiv>Google<BreakPcOnly/>アナリティクス</InfoContentDiv>
                            <InfoContentDiv>Google<BreakPcOnly/>タグマネージャ</InfoContentDiv>
                        </InfoDiv>
                    </InfoItem>
                    <InfoItem>
                        <InfoTitleDiv>送信される利用者の情報</InfoTitleDiv>
                        <InfoUl>
                            <InfoLi>インターネット通信に通常用いられるシステム、デバイス、ネットワーク、通信に関する情報</InfoLi>
                            <InfoLi>位置情報</InfoLi>
                            <InfoLi>サイト・アプリ上での行動に関するデータ</InfoLi>
                            <InfoLi>閲覧ページに関するデータ</InfoLi>
                            <InfoLi>ユーザー識別子(cookie、端末識別子等)</InfoLi>
                        </InfoUl>
                    </InfoItem>
                    <InfoItem>
                        <InfoTitleDiv>利用目的</InfoTitleDiv>
                        <InfoDiv>問題の傾向や履歴の分析のため</InfoDiv>
                    </InfoItem>
                    <InfoItem>
                        <InfoTitleDiv>送信先の利用情報</InfoTitleDiv>
                        <LinkButton onClick={this.externalTransmissionLinkClick} $mode={this.props.mode}>
                            <LeftRowFlexContainer>
                                <FontAwesomeIcon icon={faExternalLinkAlt}/>
                                <Box width={'5px'}/>
                                詳細はこちら
                            </LeftRowFlexContainer>
                        </LinkButton>
                    </InfoItem>
                    <InfoItem>
                        <InfoTitleDiv>オプトアウト</InfoTitleDiv>
                        <LinkButton onClick={this.optoutLinkClick} $mode={this.props.mode}>
                            <LeftRowFlexContainer>
                                <FontAwesomeIcon icon={faExternalLinkAlt}/>
                                <Box width={'5px'}/>
                                詳細はこちら
                            </LeftRowFlexContainer>
                        </LinkButton>
                    </InfoItem>
                </InfoContainer>
            </InfoArea>
        )
    }

    render(): Nullable<ReactElement> {
        return (
            <>
                {this.renderMainClause()}
                {this.renderTable()}
            </>
        )
    }
}

const StyledHeader = styled.div<{ $mode: 'guest' | 'resident' }>`
        font-size: ${FontSize.Accent};
        font-weight: bold;
        color: ${Colors.AccentDark};
        padding: .25rem 0 .25rem .75rem;

        ${(props) => props.$mode === 'guest' ? `
        border-left: 5px solid ${Colors.GuestPrimary};
        ` : `
        border-left: 5px solid ${Colors.Primary};
        `}
        line-height: 2;
`;

const BreakPcOnly = styled('br')`
        ${currentMedia.lessThan('sp')`
            display: none;
        `};
`

const TitleDiv: StyledDiv = styled.div`
        font-size: ${FontSize.Big};
        font-weight: bold;
        margin-top: 15px;
        color: ${Colors.AccentDark};
`;

const MainClauseDiv: StyledDiv = styled.div`
        font-size: ${FontSize.Body};
        margin-top: 20px;
`;

const InfoArea: StyledDiv = styled.div`
  background: ${Colors.Surface};
  padding: 15px;
  margin-top: 30px;
  ${currentMedia.lessThan('sp')`
    width: calc(100% + 30px);
    margin: 30px -15px 5px -15px;
    box-sizing: border-box;
    overflow: hidden;
  `};
`

const DescriptionDiv: StyledDiv = styled.div`
        font-size: ${FontSize.Body};
        margin-top: 20px;
`;

const InfoContainer: StyledDiv = styled.div`
        border: solid 1px #979797;
        display: flex;
        margin-top: 30px;
        margin-bottom: 15px;
        font-size: ${FontSize.Small};
        ${currentMedia.lessThan('sp')`
            font-size: ${FontSize.Body};
            flex-direction: column;
            border-bottom: 0;
        `};
`;

const InfoItem = styled.div`
        ${currentMedia.greaterThan('pc')`
            width: 125px;
            :nth-child(3) {
                width: 270px;
            };
            :nth-child(n+4):nth-child(-n+5) {
                width: 127px;
            };
        `};
        ${currentMedia.lessThan('sp')`
            border-bottom: solid 1px #979797;
        `};
`;

const InfoTitleDiv = styled.div`
        ${currentMedia.greaterThan('pc')`
            display: flex;
            height: 48px;
            align-items: center;
            border-bottom: solid 1px #979797;
            padding: 0 15px;
            font-weight: bold;
            background: rgba(217,217,217,0.2);
            color: ${Colors.AccentDark};
        `};
        ${currentMedia.lessThan('sp')`
            color: ${Colors.AccentDark};
            padding 10px 10px 0 10px;
        `};
`;

const InfoDiv: StyledDiv = styled.div`
        color: ${Colors.Body};
        padding 15px;
        ${currentMedia.lessThan('sp')`
            padding: 2px 10px 10px 10px;
        `};
`

const InfoContentDiv: StyledDiv = styled.div`
        ${currentMedia.greaterThan('pc')`
            padding-bottom: 10px;
        `};
`

const InfoUl = styled.ul`
        margin: 0;
        padding: 15px 15px 15px 30px;
        ${currentMedia.lessThan('sp')`
            padding-top: 2px;
            padding-right: 10px;
            padding-bottom: 10px;
        `};
`;


const InfoLi: StyledLi = styled.li`
        ::marker{
            font-size: 0.75em;
        }
`;

const LinkButton = styled(UnstyledLinkStyledButton)`
        margin: 0;
        padding-top: 15px;
        padding-left: 15px;
        display: inline-block;
        line-height: ${LineHeight.Basic};
        text-align: left;
        font-size: ${FontSize.Small};
        ${currentMedia.lessThan('sp')`
            padding: 2px 10px 10px 10px;
            font-size: ${FontSize.Body};
        `};
        ${(props) => props.$mode === 'guest' ? `
        color: ${Colors.GuestPrimary};
        ` : `
        color: ${Colors.Primary};
        `}
`;